<template>
    <div class="mt-2 divide-y-2 divide-gray-300">
        <template v-if="type === 'with-contracts' || type === 'without-contracts'">
            <template v-if="sites.length > 0">
                <contract-row v-for="site in sites" :key="site.id" :site="site" :type="type" :showInfo="showInfo"
                    @duplicated="getSites"></contract-row>
            </template>
            <div v-else>
                <p v-if="!site.id">{{ type === 'with-contracts' ? 'Aucun site avec un ou plusieurs contrats en cours.' : 'Aucun site sans contrat en cours.' }}</p>
                <p v-else>Aucun contrat en cours.</p>
                <template v-if="!!site.id">
                    <div class="mt-2">
                        <p class="font-bold mb-0">Actions</p>
                        <div class="flex items-start mt-0.5">
                            <div class="mr-3">
                                <md-button class="md-icon-button md-info" @mousedown="goToRoute($event, 'Add Site Contract', { 'idSite': site.id })">
                                    <font-awesome-icon class="md-icon" :icon="['fa', 'plus-circle']" />
                                    <span class="font-medium leading-none ml-2">Ajouter un contrat</span>
                                </md-button>
                            </div>
                            <template v-if="copiedContract.id">
                                <md-button class="md-icon-button md-default" @click="duplicateModal = true">
                                    <font-awesome-icon class="md-icon" :icon="['fa', 'paste']" />
                                    <span class="font-medium leading-none ml-2">
                                        Dupliquer le contrat :
                                        <span class="font-bold">{{ copiedContract.number }}</span>
                                    </span>
                                </md-button>
                                <md-dialog :md-active.sync="duplicateModal" :md-fullscreen="false">
                                    <md-content class="px-2 mt-3.5">
                                        <div class="px-3">
                                            <span class="text-xl font-medium">Duplication du contrat numéro <span class="text-info">{{ copiedContract.number }}</span></span>
                                            <p class="text-sm font-light">
                                                Veuillez choisir un nouveau numéro de contrat, une date de début pour la première période
                                                <br />et un montant de base.
                                            </p>
	                                        <md-divider class="my-2"></md-divider>
	                                        <p class="text-sm font-light" v-if="copiedContract.isCanceled && copiedContract.siteId === site.id">
		                                        <span class="font-medium text-info">Duplication sur le même site d'un contrat résilié détecté :</span>
		                                        les interventions planifiées après la date de résiliation seront transférées sur le nouveau contrat.
	                                        </p>
                                        </div>
                                        <div class="md-layout mt-3">
                                            <div class="md-layout-item md-size-100">
                                                <md-field class="md-invalid">
                                                    <label>Numéro</label>
                                                    <md-input v-model="contract.number" name="number" type="text" required></md-input>
                                                    <validation-error :errors="apiValidationErrors.number"/>
                                                </md-field>
                                            </div>
                                        </div>
                                        <div class="md-layout mt-4">
                                            <div class="md-layout-item md-size-50">
                                                <div class="md-vdatepicker">
                                                    <datetime v-model="contract.dateStart" type="date" auto :phrases="{ ok: 'Confirmer', cancel: 'Annuler' }"
                                                              input-id="dateStart" :placeholder="this.$moment(new Date()).format('DD MMMM YYYY')"
                                                    >
                                                        <label slot="before">Date de début de la première période *</label>
                                                    </datetime>
                                                    <validation-error :errors="apiValidationErrors.date_start" />
                                                </div>
                                            </div>
                                        </div>
                                        <div class="md-layout mt-4 mb-5">
                                            <div class="md-layout-item md-size-50">
                                                <md-field class="md-invalid">
                                                    <label>Montant de base</label>
                                                    <md-input v-model="contract.amount" name="amount" type="text" required></md-input>
                                                    <validation-error :errors="apiValidationErrors.amount"/>
                                                </md-field>
                                            </div>
                                        </div>
                                        <div class="md-layout mt-2">
                                            <div class="md-layout-item md-size-100">
                                                <span class="font-medium text-info text-lg">Détails</span>
                                            </div>
                                        </div>
                                        <div class="md-layout mt-2">
                                            <div class="md-layout-item md-size-100">
                                                <span><span class="font-medium">Site</span> : {{ site.name }}</span>
                                            </div>
                                        </div>
                                        <div class="md-layout">
                                            <div class="md-layout-item md-size-100">
                                                <span><span class="font-medium">Date de création du contrat</span> : {{ $moment(new Date()).format('DD/MM/YYYY') }}</span>
                                            </div>
                                        </div>
                                    </md-content>
                                    <md-dialog-actions>
                                        <md-button class="md-warning" @click="duplicateModal = false">Annuler</md-button>
                                        <md-button class="md-success" @click="duplicateContract">Appliquer</md-button>
                                    </md-dialog-actions>
                                </md-dialog>
                            </template>
                        </div>
                    </div>
                </template>
            </div>
        </template>
        <template v-else>
            <template v-if="completedContracts.length > 0">
                <div v-for="contract in completedContracts" :key="contract.id" class="contract-completed py-2">
                    <div class="flex items-center">
                        <div class="mr-3">
                            <md-button class="md-icon-button md-default" @click="copyContract(contract)">
                                <font-awesome-icon class="md-icon" :icon="['fa', 'copy']" />
                            </md-button>
                            <md-tooltip md-direction="top">Copier le contrat</md-tooltip>
                        </div>
                        <span class="font-bold text-info cursor-pointer mr-1"
                              @mousedown="goToRoute($event, 'Contract Details', { idSite: site.id, contractId: contract.id })">
                            {{ contract.number }}
                        </span>
                        - {{ contract.intervention_plan ? contractInterventionTypes(contract.intervention_plan.planning_intervention_groups).join(', ') : 'Aucun plan d\'intervention'}}
                    </div>
                    <div v-if="showInfo"><span class="font-bold">Site : </span>{{ contract.site.name }}</div>
                    <div class="flex items-center justify-between">
                        <div class="text-danger">
                            {{ contract.last_period.is_canceled
                            ? 'Résilié le : ' + $moment(contract.last_period.cancel_date).format('DD/MM/YYYY') + ' (' + contract.last_period.cancel_type.name + ')'
                            : 'Terminé le : ' + $moment(contract.last_period.date_end).format('DD/MM/YYYY') }}
                        </div>
                    </div>
                </div>
            </template>
            <p v-else>Aucun contrat terminé ou résilié actuellement.</p>
        </template>
    </div>
</template>

<script>
import ContractRow from "@/pages/Clients/Sites-Contracts/components/ContractRow";
import { mapState } from "vuex";
import { ValidationError } from "@/components";
import formMixin from "@/mixins/form-mixin";
import routerMixin from "@/mixins/router-mixin"

export default {
    name: "ContractTab",
    components: {
        ContractRow, ValidationError
    },
    mixins: [formMixin, routerMixin],
    props: {
        type: String,
        active: Boolean,
        site: {
            type: Object,
            default() {
                return {
                    id: null
                }
            }
        },
        showInfo: {
            type: Boolean,
            default() {
                return true;
            }
        }
    },
    data() {
        return {
            sites: [],
            completedContracts: [],
            duplicateModal: false,
            contract: {
                number: null,
                dateStart: null,
                amount: null
            },
        }
    },
    computed: mapState({
        copiedContract() {
            return this.$store.state.contracts.copied
        }
    }),
    methods: {
        async getSites() {
            const { data } = await this.$store.dispatch('sites/getSitesContracts', {
                client_id: this.$route.params.id,
                type: this.type,
                site_id: this.$route.params.idSite ?? null
            })
            this.sites = data
        },
        async getCompletedContracts() {
            const { data } = await this.$store.dispatch('contracts/getCompletedContractsFromClient', { id: this.$route.params.id })
            let contracts = data
            if(this.site.id) {
                contracts = data.filter(contract => {
                    return contract.site_id === this.site.id
                })
            }
            this.completedContracts = contracts
        },
        async copyContract(contract) {
            const { data } = await this.$store.dispatch('contracts/copyContract', { id: contract.id })
            await this.$store.dispatch('alerts/success', `<div class="flex">Contrat numéro <span class="font-bold mx-1">${data.number}</span> copié</div>`)

	        const payload = data.id ? {
		        id: data.id,
		        number: data.number,
		        siteId: data.site_id,
		        isCanceled: data.is_canceled,
	        } : null
            await this.$store.dispatch('contracts/storeCopiedContract', payload)
        },
        async duplicateContract() {
            this.resetApiValidation()
            try {
                await this.$store.dispatch('contracts/duplicateContract', {
                    site_id: this.site.id,
                    number: this.contract.number,
                    date_start: this.$moment(this.contract.dateStart).format('YYYY-MM-DD'),
                    amount: parseFloat(this.contract.amount),
                })
                this.duplicateModal = false
                this.$emit('duplicated')
                await this.$store.dispatch('alerts/success', `Contrat numéro <span class="font-bold mx-1">${this.copiedContract.number}</span> dupliqué`)
            } catch (e) {
                this.setApiValidation(e.response.data.errors)
            }
        },
        contractInterventionTypes(interventionGroups = []) {
            return interventionGroups.map(group => { return group.intervention_type.name })
        },
    },
    mounted() {
        if(this.active) {
            if (this.type === 'with-contracts' || this.type === 'without-contracts') {
                this.getSites()
            } else {
                this.getCompletedContracts()
            }
        }
    },
    watch: {
        active: function(newVal) {
            if(newVal) {
                if (this.type === 'with-contracts' || this.type === 'without-contracts') {
                    this.getSites()
                } else {
                    this.getCompletedContracts()
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.contract-completed {
    &:first-child {
        padding-top: 0;
    }
    &:last-child {
        padding-bottom: 0;
    }
}
</style>
